/**
 * Focus layers — manages focus trap, stacking, and Escape handling for overlays.
 */
window.FocusLayer = (function () {
  /** Selectors for focusable candidates, filtered by isTabbable. */
  const TABBABLE_CANDIDATES = [
    'a[href]',
    'area[href]',
    'button',
    'input',
    'select',
    'textarea',
    'summary',
    'iframe',
    'object',
    'embed',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]:not([contenteditable="false"])',
    '[tabindex]',
  ].join(',');

  /** Disclosures that handle Escape independently. */
  const OWNS_ESCAPE = 'details[open], dropdown-selects[open], dropdown-selects-options[open]';

  /** @type {Layer[]} Stack of open layers, bottom to top. */
  const stack = [];

  /** Tracks Shift+Tab direction for the focusin backstop. */
  let shiftHeld = false;

  /** Prevents keyup from closing an outer layer when Escape was handled on keydown. */
  let swallowEscapeKeyup = false;

  /** Nested disclosure given priority to handle Escape. */
  let deferredDisclosure = null;

  /** Suppresses focusin backstop while focus is actively being restored. */
  let settling = false;

  /**
   * @typedef {Object} Layer
   * @property {HTMLElement} el
   * @property {boolean} contain
   * @property {boolean} modal
   * @property {boolean} lockScroll
   * @property {HTMLElement|null} trigger - Element to restore focus to on pop
   * @property {Function|null} onDismiss - Escape handler
   */

  function isRendered(el) {
    if (!el || !el.isConnected) return false;
    if (typeof el.checkVisibility === 'function') {
      return el.checkVisibility({ checkVisibilityCSS: true, visibilityProperty: true });
    }
    return el.getClientRects().length > 0 && getComputedStyle(el).visibility !== 'hidden';
  }

  /** Ensures only the checked radio (or first if none checked) is tabbable in a group. */
  function isRedundantRadio(el, root) {
    if (el.type !== 'radio' || !el.name) return false;
    const scope = el.form || root;
    const group = scope.querySelectorAll(`input[type="radio"][name="${CSS.escape(el.name)}"]`);
    if (group.length < 2) return false;
    const checked = Array.from(group).find((radio) => radio.checked);
    return checked ? el !== checked : el !== group[0];
  }

  function isTabbable(el, root) {
    if (el.disabled) return false;
    if (el.tabIndex < 0) return false;
    if (el.type === 'hidden') return false;
    if (el.closest('[inert]')) return false;
    if (el.matches('details:not([open]) > *:not(summary) *')) return false;
    if (isRedundantRadio(el, root)) return false;
    return isRendered(el);
  }

  /** Returns tabbable elements in tab order, prioritizing positive tabIndex. */
  function tabbables(root) {
    if (!root) return [];
    const found = Array.from(root.querySelectorAll(TABBABLE_CANDIDATES)).filter((el) => isTabbable(el, root));
    const positive = found.filter((el) => el.tabIndex > 0).sort((a, b) => a.tabIndex - b.tabIndex);
    return positive.length ? [...positive, ...found.filter((el) => el.tabIndex === 0)] : found;
  }

  function top() {
    return stack.length ? stack[stack.length - 1] : null;
  }

  /** Topmost layer with containment enabled, if no uncontained layer sits above it. */
  function containingTop() {
    let index = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].contain) {
        index = i;
        break;
      }
    }
    if (index < 0) return null;

    const layer = stack[index];
    for (const above of stack.slice(index + 1)) {
      if (!layer.el.contains(above.el)) return null;
    }
    // Verify layer is still visible in case it was hidden without popping
    return isRendered(layer.el) ? layer : null;
  }

  /** True when node is inside a layer stacked above el's layer. */
  function isAboveLayerTarget(node, el) {
    const index = stack.findIndex((layer) => layer.el === el);
    if (index < 0) return false;
    return stack.slice(index + 1).some((layer) => layer.el.contains(node));
  }

  /** Finds the topmost modal layer and any stacked layers outside it that must remain accessible. */
  function isolation() {
    let index = -1;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      if (stack[i].modal) {
        index = i;
        break;
      }
    }
    if (index < 0) return null;

    const owner = stack[index];
    const outside = stack
      .slice(index + 1)
      .map((layer) => layer.el)
      .filter((el) => !owner.el.contains(el));

    return { owner, outside };
  }

  function mainLandmark() {
    return document.getElementById('MainContent') || document.querySelector('main');
  }

  /** Restores focus after pop: tries opener, then layer beneath, then main landmark. */
  function restoreFocus(layer, options) {
    settling = true;
    try {
      const boundary = containingTop();
      const candidates = [options.returnFocus, layer.trigger];
      for (const candidate of candidates) {
        if (!candidate || !candidate.isConnected || candidate.closest('[inert]') || !isRendered(candidate)) continue;
        if (boundary && !boundary.el.contains(candidate)) continue;
        candidate.focus({ preventScroll: true });
        // Confirm focus landed; document.body explicitly opts out of focus restoration
        if (document.activeElement === candidate || candidate === document.body) return;
      }
      // Fallback: focus the layer underneath if trigger is unavailable
      const beneath = top();
      if (beneath && focusInto(beneath)) return;

      // Fallback: focus main content landmark so keyboard focus doesn't reset to body
      const fallback = mainLandmark();
      if (fallback && !fallback.closest('[inert]')) {
        if (!fallback.hasAttribute('tabindex')) fallback.setAttribute('tabindex', '-1');
        fallback.focus({ preventScroll: true });
      }
    } finally {
      settling = false;
    }
  }

  /** Locks body scroll if any layer requests it, adding right padding to prevent layout shift. */
  function applyScrollLock() {
    const locked = stack.some((layer) => layer.lockScroll);
    if (locked === document.body.classList.contains('no-scroll')) return;

    if (locked) {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
      document.body.style.paddingRight = '';
    }
  }

  /** Updates aria-modal="true" on the top modal layer. */
  function applyAria() {
    const isolated = isolation();
    const claiming = isolated && !isolated.outside.length ? isolated.owner : null;

    stack.forEach((layer) => {
      if (!layer.modal) return;
      layer.el.setAttribute('aria-modal', layer === claiming ? 'true' : 'false');
    });
    const layer = top();
    if (layer && !layer.modal) layer.el.removeAttribute('aria-modal');
  }

  function setTriggerExpanded(layer, expanded) {
    const trigger = layer.trigger;
    if (!trigger || !trigger.hasAttribute('aria-expanded')) return;
    trigger.setAttribute('aria-expanded', String(expanded));
  }

  /** Focuses elementToFocus, the first tabbable, or the layer root. */
  function focusInto(layer, elementToFocus) {
    const candidates = elementToFocus ? [elementToFocus, ...tabbables(layer.el)] : tabbables(layer.el);

    for (const candidate of candidates) {
      if (!candidate || !layer.el.contains(candidate)) continue;

      candidate.focus({ preventScroll: true });
      if (document.activeElement !== candidate) continue;

      // Auto-select input text for quick replacement
      if (
        candidate.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(candidate.type) &&
        candidate.value
      ) {
        candidate.setSelectionRange(0, candidate.value.length);
      }
      return true;
    }

    if (!layer.el.hasAttribute('tabindex')) layer.el.setAttribute('tabindex', '-1');
    layer.el.focus({ preventScroll: true });
    return layer.el.contains(document.activeElement);
  }

  /** Removes disconnected or hidden layers and syncs scroll lock and aria-modal. */
  function reap() {
    let removed = false;
    for (let i = stack.length - 1; i >= 0; i -= 1) {
      const el = stack[i].el;
      if (!el.isConnected || !isRendered(el)) {
        stack.splice(i, 1);
        removed = true;
      }
    }
    if (removed) {
      applyScrollLock();
      applyAria();
    }
  }

  /**
   * Pushes a new layer to the stack and moves focus into it.
   * @param {HTMLElement} el - Layer root element
   * @param {Object} [options]
   * @param {boolean} [options.contain=true] - Keep Tab inside layer
   * @param {boolean} [options.modal=false] - Mark as modal (aria-modal)
   * @param {boolean} [options.lockScroll] - Defaults to modal
   * @param {HTMLElement} [options.initialFocus] - Defaults to first tabbable
   * @param {HTMLElement} [options.trigger] - Defaults to activeElement
   * @param {Function} [options.onDismiss] - Escape key callback
   */
  function push(el, options = {}) {
    if (!el) return null;
    reap();

    // Move layer to top if already open
    const existing = stack.findIndex((layer) => layer.el === el);
    if (existing >= 0) stack.splice(existing, 1);

    const layer = {
      el,
      contain: options.contain !== false,
      modal: options.modal === true,
      lockScroll: options.lockScroll ?? options.modal === true,
      trigger: options.trigger || (document.activeElement !== document.body ? document.activeElement : null),
      onDismiss: options.onDismiss || null,
    };
    stack.push(layer);
    applyScrollLock();
    applyAria();
    setTriggerExpanded(layer, true);

    // Flush layout so newly rendered elements can receive focus
    el.getBoundingClientRect();

    const landed = focusInto(layer, options.initialFocus);
    if (!landed) {
      // Retry once if visibility was delayed by a transition
      requestAnimationFrame(() => {
        if (stack.includes(layer)) focusInto(layer, options.initialFocus);
      });
    }

    return layer;
  }

  /**
   * Removes a layer and restores focus.
   * @param {HTMLElement} [el] - Layer to remove (defaults to top layer)
   * @param {Object} [options]
   * @param {HTMLElement} [options.returnFocus] - Custom element to receive focus
   */
  function pop(el, options = {}) {
    reap();
    const index = el ? stack.findIndex((layer) => layer.el === el) : stack.length - 1;
    if (index < 0) return;

    const [layer] = stack.splice(index, 1);
    setTriggerExpanded(layer, false);
    if (layer.modal) layer.el.removeAttribute('aria-modal');

    applyScrollLock();
    applyAria();

    // Only restore focus if the closing layer currently holds it
    const held = layer.el.contains(document.activeElement) || document.activeElement === document.body;
    if (held) restoreFocus(layer, options);
  }

  function onKeyDown(event) {
    if (event.key === 'Tab') {
      shiftHeld = event.shiftKey;
      handleTab(event);
      return;
    }

    if (event.key !== 'Escape' || event.defaultPrevented) return;

    reap();
    const layer = top();
    if (!layer) return;

    // Allow nested disclosures to close first before dismissing the layer
    const disclosure = event.target.closest?.(OWNS_ESCAPE);
    if (disclosure && disclosure !== layer.el && layer.el.contains(disclosure)) {
      deferredDisclosure = disclosure;
      return;
    }

    if (!layer.onDismiss) return;

    event.preventDefault();
    event.stopPropagation();
    swallowEscapeKeyup = true;
    layer.onDismiss();
  }

  /** Traps and wraps Tab navigation within the active containing layer. */
  function handleTab(event) {
    const layer = containingTop();
    if (!layer) return;

    const items = tabbables(layer.el);
    const active = document.activeElement;

    if (!items.length) {
      event.preventDefault();
      return;
    }

    if (!layer.el.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? items[items.length - 1] : items[0]).focus({ preventScroll: true });
      return;
    }

    const index = items.indexOf(active);
    // Let browser handle internal navigation (e.g. arrow keys between radios)
    if (index < 0) return;

    if (!event.shiftKey && index === items.length - 1) {
      event.preventDefault();
      items[0].focus({ preventScroll: true });
    } else if (event.shiftKey && index === 0) {
      event.preventDefault();
      items[items.length - 1].focus({ preventScroll: true });
    }
  }

  /** Closes unhandled <details> disclosures so Escape is never a no-op. */
  function collapseUnclaimedDisclosure() {
    const disclosure = deferredDisclosure;
    deferredDisclosure = null;
    if (!disclosure?.isConnected || !disclosure.hasAttribute('open')) return;
    if (disclosure.tagName !== 'DETAILS') return;

    disclosure.removeAttribute('open');
    const summary = disclosure.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    summary?.focus({ preventScroll: true });
  }

  function onKeyUp(event) {
    if (event.key !== 'Escape') return;

    if (deferredDisclosure) setTimeout(collapseUnclaimedDisclosure);

    if (!swallowEscapeKeyup) return;
    swallowEscapeKeyup = false;
    // Prevent ancestor disclosures from also closing on the same keyup
    event.stopPropagation();
  }

  /** Redirects focus back into the active layer if focus escapes. */
  function onFocusIn(event) {
    if (settling) return;
    const layer = containingTop();
    if (!layer || layer.el.contains(event.target)) return;

    const items = tabbables(layer.el);
    const target = shiftHeld ? items[items.length - 1] : items[0];
    (target || layer.el).focus({ preventScroll: true });
  }

  document.addEventListener('keydown', onKeyDown, true);
  document.addEventListener('keyup', onKeyUp, true);
  document.addEventListener('focusin', onFocusIn, true);

  return {
    push,
    pop,
    top,
    tabbables,
    isAboveLayerTarget,
    /** Returns layer stack data for debugging and tests. */
    inspect: () =>
      stack.map((layer) => ({
        el: layer.el.tagName.toLowerCase() + (layer.el.className ? `.${layer.el.className.split(' ')[0]}` : ''),
        contain: layer.contain,
        modal: layer.modal,
        dismissable: Boolean(layer.onDismiss),
      })),
  };
})();
